<template>
  <div class="card">
    <img class="card__image" :src="product.images.edges[0]?.node.image" :alt="product.name">
    <div class="card__view" @click="appStore.setActiveViewProduct(product)">
      <p>{{ t('buttons.quickView') }}</p>
      <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
        <path d="M2.40517 9.09141C2.32609 8.9662 2.28656 8.9036 2.26442 8.80703C2.2478 8.7345 2.2478 8.62012 2.26442 8.54758C2.28656 8.45102 2.32609 8.38842 2.40517 8.26321C3.05863 7.22851 5.00372 4.61279 7.96791 4.61279C10.9321 4.61279 12.8772 7.22851 13.5307 8.26321C13.6097 8.38842 13.6493 8.45102 13.6714 8.54758C13.688 8.62012 13.688 8.7345 13.6714 8.80703C13.6493 8.9036 13.6097 8.9662 13.5307 9.09141C12.8772 10.1261 10.9321 12.7418 7.96791 12.7418C5.00372 12.7418 3.05863 10.1261 2.40517 9.09141Z" stroke="#01AC1B" stroke-width="1.16129" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M7.96791 10.4192C8.92996 10.4192 9.70985 9.63935 9.70985 8.67731C9.70985 7.71526 8.92996 6.93537 7.96791 6.93537C7.00587 6.93537 6.22598 7.71526 6.22598 8.67731C6.22598 9.63935 7.00587 10.4192 7.96791 10.4192Z" stroke="#01AC1B" stroke-width="1.16129" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>
    <div class="card__inner">
      <router-link :to="translations.i18nRoute({ name: 'product', params: { productSlug: product.slug } })" class="card__name">{{ product.name }}</router-link>
      <div class="card__price">
        <img src="@icons/currency.svg" alt="digits">
        {{ product.price }}
      </div>
      <div class="card__bottom">
        <div @click="overwriteCart">
          <img src="@icons/cartIcon.svg" alt="cart">
          <p>{{ isProductInCart ? t('buttons.remove') : t('buttons.cart') }}</p>
        </div>
        <span></span>
        <div @click="overwriteWishlist">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="17" viewBox="0 0 18 17" fill="none">
            <path d="M9.40992 16.3264C13.7219 16.3264 17.2174 12.8309 17.2174 8.51892C17.2174 4.20696 13.7219 0.711426 9.40992 0.711426C5.09795 0.711426 1.60242 4.20696 1.60242 8.51892C1.60242 12.8309 5.09795 16.3264 9.40992 16.3264Z" stroke="#01AC1B" stroke-width="1.30125" stroke-linecap="round" stroke-linejoin="round"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M9.40724 6.22969C8.62675 5.31723 7.32521 5.07177 6.34731 5.90732C5.3694 6.74287 5.23172 8.13986 5.99968 9.12807C6.49156 9.76102 7.75128 10.9348 8.59344 11.6969C8.8733 11.9501 9.01322 12.0767 9.18083 12.1275C9.32492 12.1712 9.48957 12.1712 9.63365 12.1275C9.80126 12.0767 9.94119 11.9501 10.2211 11.6969C11.0632 10.9348 12.3229 9.76102 12.8148 9.12807C13.5828 8.13986 13.4619 6.73408 12.4672 5.90732C11.4725 5.08056 10.1877 5.31723 9.40724 6.22969Z" stroke="#01AC1B" :fill="isProductInWishlist ? 'red' : 'none'" stroke-width="1.30125" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <p>{{ t('buttons.wishlist') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {useI18n} from "vue-i18n";
import {computed} from "vue";
import {useAuthStore} from "@/stores/auth.js";
import {useCartStore} from "@/stores/cart.js";
import {useWishlistStore} from "@/stores/wishlist.js";
import {useRouter} from "vue-router";
import {ElNotification} from "element-plus";
import {Wishlist} from "@/core/api/wishlist.js";
import {Orders} from "@/core/api/orders.js";
import translations from "@/core/helpers/translations.js";
import {useAppStore} from "@/stores/app.js";

const props = defineProps({
  product: Object
})

const {t} = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()
const appStore = useAppStore()

const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : ''
})
const orderUuid = computed(() => {
  return cartStore.currentOrder ? cartStore.currentOrder.uuid : null
})
const wishlistUuid = computed(() => {
  return wishlistStore.wishlist ? wishlistStore.wishlist.uuid : ''
})
const user = computed(() => {
  return authStore.user ? authStore.user : null
})

const isProductInCart = computed(() => {
  return cartStore.currentOrder?.orderProducts?.edges.find((prod) => prod.node.product.uuid === props.product.uuid)
})
const isProductInWishlist = computed(() => {
  const el = wishlistStore.wishlist.products?.edges.find(
      (el) => el?.node?.uuid === props.product.uuid
  );

  return !!el;
});

const overwriteCart = async () => {
  if (user.value) {
    if (isProductInCart.value) {
      const res = await Orders.overwriteCart("removeOrderProduct", accessToken.value, orderUuid.value, props.product.uuid);

      cartStore.setCurrentOrders(res.data.removeOrderProduct.order)
    } else {
      const res = await Orders.overwriteCart("addOrderProduct", accessToken.value, orderUuid.value, props.product.uuid);
      cartStore.setCurrentOrders(res.data.addOrderProduct.order)

      if (res.errors) {
        ElNotification({
          message: res.errors[0].message,
          type: "error"
        });
      } else {
        ElNotification({
          message: t('popup.addToCart', { product: props.product.name }),
          type: "success"
        });
      }
    }
  } else {
    await router.push({
      name: 'login',
      params: {
        locale: localStorage.getItem('gift-digital-user-locale') || 'en-gb'
      }
    })
  }
}

const overwriteWishlist = async () => {
  if (user.value) {
    if (isProductInWishlist.value) {
      const res = await Wishlist.overwriteWishlist('removeWishlistProduct', accessToken.value, wishlistUuid.value, props.product.uuid)
      wishlistStore.wishlist = res.data.removeWishlistProduct.wishlist
    } else {
      const res = await Wishlist.overwriteWishlist('addWishlistProduct', accessToken.value, wishlistUuid.value, props.product.uuid)
      wishlistStore.wishlist = res.data.addWishlistProduct.wishlist

      if (res.data.addWishlistProduct) {
        ElNotification({
          message: t('popup.addToWishlist', { product: props.product.name }),
          type: "success"
        });
      } else {
        ElNotification({
          message: res.errors[0].message,
          type: "error"
        });
      }
    }
  } else {
    await router.push({
      name: 'auth-login',
      params: {
        locale: localStorage.getItem('gift-digital-user-locale') || 'en-gb'
      }
    })
  }
}
</script>

<style lang="scss" scoped>
.card {
  width: 100%;
  border-radius: 16px;
  border: 1px solid #EDEDED;
  overflow: hidden;
  transition: 0.2s;
  position: relative;

  @include hover {
    border-color: #01AC1B;
    box-shadow: 0 151px 42px 0 rgba(0, 0, 0, 0.00), 0 97px 39px 0 rgba(0, 0, 0, 0.01), 0 54px 33px 0 rgba(0, 0, 0, 0.03), 0 24px 24px 0 rgba(0, 0, 0, 0.04), 0 6px 13px 0 rgba(0, 0, 0, 0.05), 0 0 0 0 rgba(0, 0, 0, 0.05);

    & .card__view {
      opacity: 1;
    }
  }

  &__view {
    cursor: pointer;
    position: absolute;
    top: 10px;
    right: 10px;
    border-radius: 15px;
    background-color: #F3F9FB;
    box-shadow: 2px 3px 4px 0 rgba(0, 0, 0, 0.10);
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 7px 12px;
    opacity: 0;
    transition: 0.2s;

    & p {
      transition: 0.2s;
      color: #222;
      font-size: 12px;
      font-weight: 500;
      line-height: 15px;
    }

    @include hover {
      background-color: #dadada;

      & p {
        color: #008ECC;
      }
    }
  }

  &__image {
    width: 100%;
    object-fit: cover;
  }

  &__inner {
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__name {
    transition: 0.2s;
    word-break: break-all;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    overflow: hidden;
    color: #222;
    font-size: 16px;
    font-weight: 600;
    line-height: 18px;

    @include hover {
      color: #01AC1B;
    }
  }

  &__price {
    display: flex;
    align-items: center;
    gap: 3px;

    color: #222;
    font-size: 16px;
    font-weight: 700;
    line-height: 18px;
  }

  &__bottom {
    border-top: 1px solid #EDEDED;
    padding-top: 7px;
    padding-right: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;

    & span {
      width: 1px;
      height: 20px;
      background-color: #D9D9D9;
    }

    & div {
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 5px;

      @include hover {
        & p {
          color: #01AC1B;
        }
      }

      & p {
        transition: 0.2s;
        color: #666;
        font-size: 14px;
        font-weight: 700;
        line-height: 15.614px;
      }
    }
  }
}
</style>